import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

// Admin Management → Announcements. Writes the Broadcast record; the recipient
// rows it produces are what the floating notifier reads.

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

const message = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.errors?.join?.(", ") ||
  error.response?.data?.message ||
  fallback;

const initialState = {
  // Tours available to attach as an announcement's action.
  tours: [],
  toursLoading: false,
  // The people the picker's current filters resolve to, one page of them, plus
  // every matching id for "select all".
  recipients: [],
  recipientIds: [],
  recipientsMeta: {},
  recipientsLoading: false,
  // Who one sent announcement went to, with how it went for each.
  deliveries: [],
  deliveriesMeta: {},
  deliveriesLoading: false,
  // Accounts with their groups and who is in each, for the picker's left side,
  // and the roles that have somebody emailable.
  accounts: [],
  accountRoles: [],
  accountsLoading: false,
  list: [],
  meta: {},
  listLoading: false,
  current: null,
  currentLoading: false,
  saving: false,
  testSent: false,
  dispatching: false,
};

export const fetchBroadcasts = createAsyncThunk(
  "broadcasts/fetchAll",
  async ({ page = 1, perPage = 15, status } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/broadcasts`, {
        headers: authHeaders(),
        params: { page, per_page: perPage, status },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load announcements"));
    }
  }
);

export const fetchBroadcast = createAsyncThunk(
  "broadcasts/fetchOne",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/broadcasts/${id}`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load announcement"));
    }
  }
);

// Attachments mean the save has to be multipart, so the payload is flattened
// into FormData rather than sent as JSON.
const toFormData = (broadcast, files = []) => {
  const form = new FormData();

  form.append("broadcast[title]", broadcast.title ?? "");
  form.append("broadcast[subject_line]", broadcast.subject_line ?? "");
  form.append("broadcast[body_html]", broadcast.body_html ?? "");
  form.append("broadcast[action_type]", broadcast.action_type ?? "no_action");
  form.append("broadcast[tour_id]", broadcast.tour_id ?? "");
  form.append("broadcast[resource_file_id]", broadcast.resource_file_id ?? "");

  (broadcast.channels || []).forEach((channel) =>
    form.append("broadcast[channels][]", channel)
  );
  (broadcast.audience?.roles || []).forEach((role) =>
    form.append("broadcast[audience][roles][]", role)
  );
  (broadcast.audience?.user_ids || []).forEach((id) =>
    form.append("broadcast[audience][user_ids][]", id)
  );
  // Which accounts were chosen, so reopening a draft shows them again. Who is
  // actually reached is still decided by user_ids alone.
  (broadcast.audience?.company_ids || []).forEach((id) =>
    form.append("broadcast[audience][company_ids][]", id)
  );
  // Always sent, even when empty — an empty selection must mean nobody, not
  // the staff-wide default.
  if (!(broadcast.audience?.user_ids || []).length) {
    form.append("broadcast[audience][user_ids][]", "");
  }
  // An audience with no roles still has to reach the server as an empty list,
  // or Rails sees no audience key at all and falls back to "all staff".
  if (!(broadcast.audience?.roles || []).length) {
    form.append("broadcast[audience][roles][]", "");
  }

  files.forEach((file) => form.append("broadcast[attachments][]", file));
  return form;
};

const multipartHeaders = () => ({
  "Content-Type": "multipart/form-data",
  Authorization: `Bearer ${getToken()}`,
});

export const createBroadcast = createAsyncThunk(
  "broadcasts/create",
  async ({ broadcast, files }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/broadcasts`,
        toFormData(broadcast, files),
        { headers: multipartHeaders() }
      );
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to save announcement");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const updateBroadcast = createAsyncThunk(
  "broadcasts/update",
  async ({ id, broadcast, files }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${apiBaseUrl}/admin/broadcasts/${id}`,
        toFormData(broadcast, files),
        { headers: multipartHeaders() }
      );
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to save announcement");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const removeBroadcastAttachment = createAsyncThunk(
  "broadcasts/removeAttachment",
  async ({ id, attachmentId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${apiBaseUrl}/admin/broadcasts/${id}/attachments/${attachmentId}`,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to remove attachment");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// Tours the composer can attach as an announcement's action.
export const fetchTours = createAsyncThunk(
  "broadcasts/fetchTours",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/tours`, {
        headers: authHeaders(),
      });
      return response.data?.tours || [];
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load tours"));
    }
  }
);

// Quill inserts an image as base64 by default, which Gmail and Outlook strip.
// Upload it and insert the returned URL instead.
export const uploadInlineImage = async (file) => {
  const form = new FormData();
  form.append("file", file);

  const response = await axios.post(
    `${apiBaseUrl}/admin/broadcast_images`,
    form,
    { headers: multipartHeaders() }
  );
  return response.data?.url;
};

export const deleteBroadcast = createAsyncThunk(
  "broadcasts/delete",
  async ({ id }, { rejectWithValue }) => {
    try {
      await axios.delete(`${apiBaseUrl}/admin/broadcasts/${id}`, {
        headers: authHeaders(),
      });
      toast.success("Announcement deleted");
      return { id };
    } catch (error) {
      const msg = message(error, "Failed to delete announcement");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// One page of the people an announcement went to, for the "who received it" view.
export const fetchBroadcastDeliveries = createAsyncThunk(
  "broadcasts/deliveries",
  async ({ id, q, page = 1 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/broadcasts/${id}/recipients`, {
        headers: authHeaders(),
        params: { q, page, per_page: 25 },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load recipients"));
    }
  }
);

// Accounts that have someone to email, each with its groups.
export const fetchBroadcastAccounts = createAsyncThunk(
  "broadcasts/accounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/broadcasts/accounts`, {
        headers: authHeaders(),
      });
      return response.data || {};
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load accounts"));
    }
  }
);

// The people in the chosen accounts (or one group of them), so each can be ticked.
export const fetchRecipients = createAsyncThunk(
  "broadcasts/recipients",
  async ({ q, role, page = 1, companyIds, groupId, broadcastId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/admin/broadcasts/recipients`, {
        headers: authHeaders(),
        // broadcastId makes each row say whether it already has that announcement.
        params: {
          q,
          role,
          page,
          per_page: 25,
          company_ids: companyIds,
          group_id: groupId,
          broadcast_id: broadcastId,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(message(error, "Failed to load recipients"));
    }
  }
);

export const sendBroadcastTest = createAsyncThunk(
  "broadcasts/sendTest",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/broadcasts/${id}/send_test`,
        {},
        { headers: authHeaders() }
      );
      toast.success(response.data?.message || "Test email sent");
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to send test");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// A draft goes to its saved audience. For one that was already sent, `audience`
// names the people to send it to now (new, or again).
export const dispatchBroadcast = createAsyncThunk(
  "broadcasts/dispatch",
  async ({ id, audience }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/broadcasts/${id}/dispatch_now`,
        audience ? { audience } : {},
        { headers: authHeaders() }
      );
      toast.success(response.data?.message || "Announcement queued");
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to send announcement");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

// Re-queues a send that stalled part-way — a worker restarted mid-batch, an
// SMTP outage. Only the recipients still pending are picked up.
export const resumeBroadcast = createAsyncThunk(
  "broadcasts/resume",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/broadcasts/${id}/resume`,
        {},
        { headers: authHeaders() }
      );
      toast.success(response.data?.message || "Delivery resumed");
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to resume delivery");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const cancelBroadcast = createAsyncThunk(
  "broadcasts/cancel",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/broadcasts/${id}/cancel`,
        {},
        { headers: authHeaders() }
      );
      toast.success("Announcement cancelled");
      return response.data;
    } catch (error) {
      const msg = message(error, "Failed to cancel announcement");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  }
);

export const broadcastSlice = createSlice({
  name: "broadcasts",
  initialState,
  reducers: {
    clearCurrentBroadcast: (state) => {
      state.current = null;
      state.testSent = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBroadcasts.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(fetchBroadcasts.fulfilled, (state, action) => {
        state.listLoading = false;
        state.list = action.payload?.broadcasts || [];
        state.meta = action.payload?.meta || {};
      })
      .addCase(fetchBroadcasts.rejected, (state) => {
        state.listLoading = false;
      })
      .addCase(fetchBroadcast.pending, (state) => {
        state.currentLoading = true;
      })
      .addCase(fetchBroadcast.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchBroadcast.rejected, (state) => {
        state.currentLoading = false;
      })
      .addCase(createBroadcast.pending, (state) => {
        state.saving = true;
      })
      .addCase(createBroadcast.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(createBroadcast.rejected, (state) => {
        state.saving = false;
      })
      .addCase(updateBroadcast.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateBroadcast.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
      })
      .addCase(updateBroadcast.rejected, (state) => {
        state.saving = false;
      })
      .addCase(deleteBroadcast.fulfilled, (state, action) => {
        state.list = state.list.filter((b) => b.id !== action.payload.id);
      })
      .addCase(removeBroadcastAttachment.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(fetchTours.pending, (state) => {
        state.toursLoading = true;
      })
      .addCase(fetchTours.fulfilled, (state, action) => {
        state.toursLoading = false;
        state.tours = action.payload;
      })
      .addCase(fetchTours.rejected, (state) => {
        state.toursLoading = false;
      })
      .addCase(sendBroadcastTest.fulfilled, (state) => {
        // Unlocks Send. The admin has now seen exactly what recipients will get.
        state.testSent = true;
      })
      .addCase(dispatchBroadcast.pending, (state) => {
        state.dispatching = true;
      })
      .addCase(dispatchBroadcast.fulfilled, (state, action) => {
        state.dispatching = false;
        state.current = action.payload;
      })
      .addCase(dispatchBroadcast.rejected, (state) => {
        state.dispatching = false;
      })
      .addCase(cancelBroadcast.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(resumeBroadcast.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(fetchRecipients.pending, (state) => {
        state.recipientsLoading = true;
      })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.recipientsLoading = false;
        state.recipients = action.payload?.recipients || [];
        state.recipientIds = action.payload?.ids || [];
        state.recipientsMeta = action.payload?.meta || {};
      })
      .addCase(fetchRecipients.rejected, (state) => {
        state.recipientsLoading = false;
      })
      .addCase(fetchBroadcastDeliveries.pending, (state) => {
        state.deliveriesLoading = true;
      })
      .addCase(fetchBroadcastDeliveries.fulfilled, (state, action) => {
        state.deliveriesLoading = false;
        state.deliveries = action.payload?.recipients || [];
        state.deliveriesMeta = action.payload?.meta || {};
      })
      .addCase(fetchBroadcastDeliveries.rejected, (state) => {
        state.deliveriesLoading = false;
      })
      .addCase(fetchBroadcastAccounts.pending, (state) => {
        state.accountsLoading = true;
      })
      .addCase(fetchBroadcastAccounts.fulfilled, (state, action) => {
        state.accountsLoading = false;
        state.accounts = action.payload.accounts || [];
        state.accountRoles = action.payload.roles || [];
      })
      .addCase(fetchBroadcastAccounts.rejected, (state) => {
        state.accountsLoading = false;
      });
  },
});

export const { clearCurrentBroadcast } = broadcastSlice.actions;

export default broadcastSlice.reducer;
